import React, { useLayoutEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import styled from "styled-components";
import "./styles/App.css";
// Import the six PNG images with Chinese names
import image3 from './assets/images/未命名作品 3.png';
import image5 from './assets/images/未命名作品 5.png';
import meGif from './assets/images/me.GIF';
import pigGif from './assets/images/pig.GIF';
import cameraGIF from './assets/images/camera.GIF'
import pianoGIF from './assets/images/music.GIF';

const LandingContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 95vh;
  width: 100vw;
  max-width: 100%;
  background: white;
  overflow: hidden;
  box-sizing: border-box;
  padding-top: 15vh;
  
  @media (max-width: 768px) {
    padding-top: 2rem;
    justify-content: flex-start;
    height: auto;
  }
`;

const TextZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 60%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 800px) {
    width: 80%;
  }
`;

const CenterText = styled.div`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 500;
  color: #384959;
  font-family: 'Inter', sans-serif;
  font-optical-sizing: auto;
  line-height: 1.3;
  max-width: 700px;
  padding: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    line-height: 1.5;
    padding: 0.5rem;
  }
`;

const CenterText2 = styled.div`
  padding-top: 1rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 400;
  color: #384959;
  font-family: 'Inter', sans-serif;
  font-optical-sizing: auto;
  line-height: 1.3;
  max-width: 700px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    line-height: 1.5;
    padding: 0.5rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  padding-top: 1.5rem;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;

  a {
    color: #6A89A7;
    text-decoration: none;
    transition: color 0.3s ease;
    font-weight: 400;

    &:hover {
      text-decoration: underline;
      color: #384959;
    }
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    gap: 1rem;
    padding-top: 1rem;
    flex-wrap: wrap;
  }
`;

const DecorImage = styled(motion.img)`
  position: absolute;
  border-radius: 10px;
  object-fit: cover;
  
  @media (max-width: 768px) {
    position: static;
    width: 100%;
    height: auto;
    aspect-ratio: 1 / 1;
  }
`;

const ImageZone = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  
  ${DecorImage} {
    pointer-events: auto;
  }
  
  @media (max-width: 768px) {
    position: static;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    padding: 2rem 1rem;
    max-width: 100%;
    height: auto;
    pointer-events: auto;
  }
`;

/**
 * @typedef {Object} FloatingImage
 * @property {string} id
 * @property {string} src
 * @property {number} widthVw
 * @property {number} aspectRatio
 */

/**
 * Get dynamic width based on viewport size
 * Larger screens = smaller images (relatively)
 * @param {number} baseWidth - Base width in vw units
 * @returns {number} Adjusted width in vw
 */
function getDynamicWidth(baseWidth) {
  const vw = window.innerWidth;

  
  // For very large screens (>1920px), scale down to 60% of base
  if (vw > 1920) return baseWidth * 0.6;
  
  // For large screens (1440-1920px), scale down to 75% of base
  if (vw > 1440) return baseWidth * 0.75;
  
  // For medium screens (1024-1440px), scale down to 85% of base
  if (vw > 1024) return baseWidth * 0.85;
  
  // For smaller screens, use full base size
  return baseWidth;
}

/** @type {FloatingImage[]} */
const getFloatingImages = () => [
  {id: "1", src: pigGif, widthVw: getDynamicWidth(20), aspectRatio: 1},
  {id: "2", src: pianoGIF, widthVw: getDynamicWidth(20), aspectRatio: 1},
  {id: "3", src: image3, widthVw: getDynamicWidth(20), aspectRatio: 1, Link: "/Photography#beijing"},
  {id: "4", src: meGif, widthVw: getDynamicWidth(30), aspectRatio: 1, Link: "/Projects"},
  {id: "5", src: image5, widthVw: getDynamicWidth(18), aspectRatio: 1},
  {id: "6", src: cameraGIF, widthVw: getDynamicWidth(20), aspectRatio: 1, Link: "/Photography"},
];

/**
 * @typedef {Object} Position
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {Record<string, Position>} PositionsMap
 */

/**
 * Fixed positions for scattered layout (viewport percentages)
 * Desktop: artistic scattered look around the text zone
 */
const FIXED_LAYOUTS = {
  "1": { xPercent: 8, yPercent: 25 },   // pig
  "2": { xPercent: 88, yPercent: 16 },   // piano
  "3": { xPercent: 8, yPercent: 55 },    // beijing
  "4": { xPercent: 52, yPercent: 60 },   // me
  "5": { xPercent: 30, yPercent: 60 },   // painting
  "6": { xPercent: 90, yPercent: 45 },   // camera
};

/**
 * Fixed positions for mobile layout (viewport percentages)
 * Mobile: scattered below the text with better spacing
 */
const MOBILE_FIXED_LAYOUTS = {
  "1": { xPercent: 5, yPercent: 45 },     // pig - upper left
  "2": { xPercent: 70, yPercent: 45 },    // piano - upper right
  "3": { xPercent: 25, yPercent: 57 },    // beijing - middle left
  "4": { xPercent: 50, yPercent: 55 },    // me - middle right
  "5": { xPercent: 10, yPercent: 75 },     // painting - lower left
  "6": { xPercent: 50, yPercent: 75 },    // camera - lower right
};

/**
 * Custom hook to calculate fixed but responsive positions for floating images
 * @param {DOMRect | null} textRect
 * @param {FloatingImage[]} images
 * @param {boolean} isMobile
 * @returns {PositionsMap}
 */
function useFloatingImagePositions(textRect, images, isMobile) {
  const [positions, setPositions] = useState({});

  useLayoutEffect(() => {
    if (!textRect) return;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const nextPositions = {};

    for (const img of images) {
      const width = (img.widthVw / 100) * vw;
      const height = width / img.aspectRatio;

      let x, y;

      if (isMobile) {
        // Mobile: simple viewport-based positioning
        const mobileLayout = MOBILE_FIXED_LAYOUTS[img.id];
        if (!mobileLayout) continue;
        
        x = (mobileLayout.xPercent / 100) * vw;
        y = (mobileLayout.yPercent / 100) * vh;
        
        // Keep within bounds with smaller padding
        const borderPadding = 10;
        x = Math.max(borderPadding, Math.min(x, vw - width - borderPadding));
        y = Math.max(borderPadding, Math.min(y, vh - height - borderPadding));
      } else {
        // Desktop: use desktop fixed layout
        const layout = FIXED_LAYOUTS[img.id];
        if (!layout) continue;
        
        x = (layout.xPercent / 100) * vw;
        y = (layout.yPercent / 100) * vh;

        // Keep within bounds
        const borderPadding = Math.max(vw * 0.05, 60);
        x = Math.max(borderPadding, Math.min(x, vw - width - borderPadding));
        y = Math.max(borderPadding, Math.min(y, vh - height - borderPadding));
      }

      nextPositions[img.id] = { x, y };
    }

    setPositions(nextPositions);
  }, [textRect, images, isMobile]);

  return positions;
}


const MOBILE_BREAKPOINT = 768;

function Home() {
  const navigate = useNavigate();
  const textRef = useRef(null);
  const [textRect, setTextRect] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
  );
  const [floatingImages, setFloatingImages] = useState(() => getFloatingImages());
  
  // Handle image clicks - internal vs external navigation
  const handleImageClick = (link) => {
    if (!link) return;
    
    // Check if it's an internal link (starts with /)
    if (link.startsWith('/')) {
      // For internal links with hash, use full URL to ensure navigation and scroll
      if (link.includes('#')) {
        window.location.href = window.location.origin + link;
      } else {
        navigate(link);
      }
    } else {
      // External link - open in new tab
      window.open(link, '_blank');
    }
  };
  
  // Measure text position and handle resize
  useLayoutEffect(() => {
    function updateLayout() {
      if (textRef.current) {
        setTextRect(textRef.current.getBoundingClientRect());
      }
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
      // Recalculate image sizes on resize
      setFloatingImages(getFloatingImages());
    }
    
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);
  
  // Calculate positions for images
  const positions = useFloatingImagePositions(
    textRect,
    floatingImages,
    isMobile
  );

  return (
    <LandingContainer>
      {/* Text zone - separate from images */}
      <TextZone ref={textRef}>
        <CenterText>
          Hi, I'm Diana :) I'm an engineer at Microsoft, prev @Datadog, The Fabricant<br />
        </CenterText>
        <CenterText2>
        I grew up in Beijing and Ohio.
        I recently graduated from Harvard with a degree in Computer Science and Philosophy.
        I'm passionate about exploring how technology can improve ~ human flourishing ~.

        </CenterText2>
        <SocialLinks>
          <a href="mailto:diana.yue24@gmail.com">email</a>
          <a href="https://dandanbored.substack.com/" target="_blank" rel="noopener noreferrer">substack</a>
          <a href="https://www.linkedin.com/in/diana-yue-89588a142/" target="_blank" rel="noopener noreferrer">linkedin</a>
        </SocialLinks>
      </TextZone>
      
      {/* Scattered floating images (desktop and mobile) */}
      <ImageZone>
        {floatingImages.map((img) => {
          const pos = positions[img.id];
          if (!pos) return null;
          
          const vw = window.innerWidth;
          const width = (img.widthVw / 100) * vw;
          const height = width / img.aspectRatio;
          
          // On mobile, don't apply absolute positioning
          const style = isMobile 
            ? { cursor: img.Link ? 'pointer' : 'default' }
            : {
                left: `${pos.x}px`,
                top: `${pos.y}px`,
                width: `${width}px`,
                height: `${height}px`,
                cursor: img.Link ? 'pointer' : 'default',
              };
          
          return (
            <DecorImage
              key={img.id}
              src={img.src}
              alt={`Decoration ${img.id}`}
              style={style}
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                y: isMobile ? [0, -4, 0] : [0, -8, 0, 6, 0],
                x: isMobile ? 0 : [0, 4, -4, 0],
              }}
              transition={{
                opacity: { duration: 0.5 },
                y: {
                  duration: isMobile ? 6 + Math.random() * 3 : 8 + Math.random() * 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
                x: {
                  duration: 8 + Math.random() * 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                },
              }}
              whileHover={{ scale: 1.05 }}
              onClick={() => handleImageClick(img.Link)}
            />
          );
        })}
      </ImageZone>
    </LandingContainer>
  );
}

export default Home;
